// ParticlesBackground.jsx
import React, { useState, useEffect, useMemo } from "react";
import { motion, useReducedMotion } from "framer-motion";

const clamp = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(value, max));

const ParticlesBackground = ({
  count = 90,
  colorRgb = "255,255,255",
  minSize = 2,
  maxSize = 8,
  speedMin = 10,
  speedMax = 22,
  debug = false,
}) => {
  const reduceMotion = useReducedMotion();
  const [height, setHeight] = useState(0);

  useEffect(() => {
    const updateHeight = () => setHeight(window.innerHeight || document.documentElement.clientHeight);
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  const particles = useMemo(() => {
    const n = debug ? Math.min(40, count) : count;
    return Array.from({ length: n }).map((_, i) => {
      const size = clamp(minSize + Math.random() * (maxSize - minSize), minSize, maxSize);
      return {
        id: i,
        size,
        left: Math.random() * 100,
        // small random start offset so particles don't all begin at exactly 100%
        startOffset: Math.random() * 6, // 0 - 6%
        // delay shorter in debug
        delay: Math.random() * (debug ? 3 : 20),
        duration: speedMin + Math.random() * (speedMax - speedMin),
        alpha: 0.25 + Math.random() * 0.7,
      };
    });
  }, [count, debug, minSize, maxSize, speedMin, speedMax]);

  if (height === 0) return null;

  return (
    <div className="absolute inset-0 pointer-events-none z-0 overflow-hidden" aria-hidden>
      {particles.map((p) => (
        <motion.div
          key={p.id}
          initial={{ opacity: 0.25, translateY: 0 }}
          animate={
            reduceMotion
              ? { opacity: [0.5, 0.5] } // minimal/no motion
              : { translateY: -height - p.size - 80, opacity: [0.25, 1, 0.25] }
          }
          transition={{
            duration: reduceMotion ? 0.001 : p.duration,
            repeat: reduceMotion ? 0 : Infinity,
            delay: p.delay,
            ease: "linear",
          }}
          style={{
            position: "absolute",
            left: `${p.left}%`,
            top: `${100 + p.startOffset}%`,
            width: p.size,
            height: p.size,
            borderRadius: 9999,
            backgroundColor: `rgba(${colorRgb}, ${p.alpha})`,
            boxShadow: `0 0 ${Math.max(1, p.size)}px rgba(${colorRgb}, ${p.alpha * 0.9})`,
            willChange: "transform, opacity",
            pointerEvents: "none",
          }}
        />
      ))}
    </div>
  );
};

export default ParticlesBackground;